import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Chip, Grid,
    TextField, Switch, Button, Alert, CircularProgress, MenuItem
} from "@mui/material";
import RedeemIcon from "@mui/icons-material/Redeem";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import StorefrontIcon from "@mui/icons-material/Storefront";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";

const EditBonusDialog = ({ open, bonus, onClose, onSaved }) => {
    const [form, setForm] = useState({ bonus_key: "", name: "", description: "", award_policy: "", policy_params: "", active: true });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [policiesCatalog, setPoliciesCatalog] = useState([]);

    useEffect(() => {
        if (open && bonus?.brand) {
            fetch(`${LOYALTY_API_BASE}/admin/bonus-award-policies`, { headers: { "X-Brand": bonus.brand } })
                .then(r => r.json())
                .then(d => setPoliciesCatalog(d.policies || []))
                .catch(e => console.error(e));
        }
    }, [open, bonus]);

    useEffect(() => {
        if (bonus) {
            setForm({
                bonus_key: bonus.bonus_key || "",
                name: bonus.name || "",
                description: bonus.description || "",
                award_policy: bonus.award_policy || "",
                policy_params: bonus.policy_params ? JSON.stringify(bonus.policy_params, null, 2) : "",
                active: bonus.active ?? true,
            });
            setError(null);
        }
    }, [bonus]);

    const handle = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));
    const isValid = form.bonus_key.trim() && form.name.trim() && form.award_policy.trim();

    const handleSubmit = async () => {
        if (!isValid || !bonus) return;
        setSubmitting(true);
        setError(null);
        try {
            let policyParams = null;
            if (form.policy_params.trim()) {
                try { policyParams = JSON.parse(form.policy_params); }
                catch { throw new Error("policy_params n'est pas un JSON valide"); }
            }
            const body = {
                bonus_key: form.bonus_key.trim(),
                name: form.name.trim(),
                description: form.description.trim() || null,
                award_policy: form.award_policy.trim(),
                policy_params: policyParams,
                active: form.active,
            };
            const res = await fetch(`${LOYALTY_API_BASE}/admin/bonus-definitions/${bonus.id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "X-Brand": bonus.brand
                },
                body: JSON.stringify(body),
            });
            if (!res.ok) { const e = await res.json().catch(() => null); throw new Error(e?.detail || `Erreur HTTP ${res.status}`); }
            onSaved(); onClose();
        } catch (err) { setError(err.message); } finally { setSubmitting(false); }
    };

    if (!bonus) return null;
    const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.bonus.gradient }}>
                        <RedeemIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                            Modifier la Définition de Bonus
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">{bonus.brand ? `${bonus.brand} • ` : ""}{bonus.name}</Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                {/* Brand (read-only) */}
                <Box display="flex" alignItems="center" gap={1} mb={2.5} px={1.5} py={1}
                    sx={{ borderRadius: 2, bgcolor: "#f4fdfb", border: "1px solid #b2ebe0" }}>
                    <StorefrontIcon sx={{ fontSize: 18, color: SECTION_COLORS.bonus.main }} />
                    <Typography fontSize={13} color="text.secondary">Marque :</Typography>
                    <Chip label={bonus.brand || "Globale"} size="small"
                        sx={{ fontWeight: 700, fontSize: 12, bgcolor: `${SECTION_COLORS.bonus.main}15`, color: SECTION_COLORS.bonus.main }} />
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth required label="Clé bonus" value={form.bonus_key}
                            onChange={handle("bonus_key")} size="small" helperText="Identifiant technique" sx={fieldSx} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth required label="Nom" value={form.name}
                            onChange={handle("name")} size="small" sx={fieldSx} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth required select label="Politique d'attribution" value={form.award_policy}
                            onChange={handle("award_policy")} size="small" sx={fieldSx}
                            helperText="Sélectionnez la politique d'attribution">
                            {policiesCatalog.map((p) => (
                                <MenuItem key={p.type} value={p.type}>
                                    <Box>
                                        <Typography variant="body2" fontWeight={600}>{p.title || p.type}</Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">{p.description}</Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Description" value={form.description}
                            onChange={handle("description")} size="small" multiline rows={2} sx={fieldSx} />
                    </Grid>

                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}
                            sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Box>
                                <Typography fontSize={13} fontWeight={600}>Activer</Typography>
                                <Typography fontSize={11} color="text.secondary">Disponible immédiatement</Typography>
                            </Box>
                            <Switch checked={form.active} onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.bonus.main },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.bonus.main }
                                }} />
                        </Box>
                    </Grid>
                </Grid>
                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={onClose} sx={{ color: "text.secondary", textTransform: "none" }}>Annuler</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!isValid || submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    sx={{ background: SECTION_COLORS.bonus.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3 }}>
                    {submitting ? "Enregistrement..." : "Enregistrer"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditBonusDialog;
